import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, Eye, BookOpen } from "lucide-react";
import { BlockEditor, BLOCK_TYPES, LessonBlockRenderer } from "@/components/LessonBlocks";
import TagsInput from "@/components/TagsInput";

const newId = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 10)}`;

export default function CourseEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [course, setCourse] = useState({
    title: "", description: "", level: "Beginner", duration_hours: 0, tags: [],
    price_npr: 2000, cover_image_url: "",
    partner_student_pct_off: 0, partner_faculty_pct_off: 0, free_for_partner_students: false,
    modules: [], status: "draft",
  });
  const [saving, setSaving] = useState(false);
  const [openMod, setOpenMod] = useState({});
  const [openLes, setOpenLes] = useState({});
  const [activeLes, setActiveLes] = useState(null);
  const [showAddBlock, setShowAddBlock] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/courses/${id}`).then((r) => setCourse(r.data)).catch(() => toast.error("Couldn't load"));
  }, [id, isNew]);

  const save = async (asPublished = null) => {
    if (!course.title?.trim()) {
      toast.error("Add a course title before saving");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...course };
      if (asPublished !== null) payload.status = asPublished ? "published" : "draft";
      let saved;
      if (isNew) {
        const r = await api.post("/courses", payload);
        saved = r.data;
        toast.success("Course created");
        navigate(`/dashboard/courses/${saved.id}/edit`, { replace: true });
      } else {
        const r = await api.put(`/courses/${id}`, payload);
        saved = r.data;
        toast.success(asPublished === true ? "Published" : asPublished === false ? "Unpublished" : "Saved");
      }
      setCourse(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const submitForReview = async () => {
    if (isNew) { toast.error("Save the course first"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/courses/${id}/submit-for-review`);
      setCourse(r.data);
      toast.success("Submitted for admin review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally { setSaving(false); }
  };

  const courseId = isNew ? null : (course.id || id);

  const addModule = () => {
    const mod = { id: newId("mod"), title: `Module ${course.modules.length + 1}`, lessons: [] };
    setCourse((c) => ({ ...c, modules: [...c.modules, mod] }));
    setOpenMod((o) => ({ ...o, [mod.id]: true }));
  };
  const updateModule = (mid, patch) => {
    setCourse((c) => ({ ...c, modules: c.modules.map(m => m.id === mid ? { ...m, ...patch } : m) }));
  };
  const deleteModule = (mid) => {
    if (!window.confirm("Delete this module?")) return;
    setCourse((c) => ({ ...c, modules: c.modules.filter(m => m.id !== mid) }));
  };
  const addLesson = (mid) => {
    const less = { id: newId("les"), title: "New lesson", blocks: [], duration_min: 10 };
    setCourse((c) => ({
      ...c, modules: c.modules.map(m => m.id === mid ? { ...m, lessons: [...(m.lessons || []), less] } : m)
    }));
    setOpenLes((o) => ({ ...o, [less.id]: true }));
    setActiveLes({ mid, lid: less.id });
  };
  const updateLesson = (mid, lid, patch) => {
    setCourse((c) => ({
      ...c, modules: c.modules.map(m => m.id === mid ? {
        ...m, lessons: (m.lessons || []).map(l => l.id === lid ? { ...l, ...patch } : l)
      } : m)
    }));
  };
  const deleteLesson = (mid, lid) => {
    if (!window.confirm("Delete this lesson?")) return;
    setCourse((c) => ({
      ...c, modules: c.modules.map(m => m.id === mid ? {
        ...m, lessons: (m.lessons || []).filter(l => l.id !== lid)
      } : m)
    }));
  };

  const addBlock = async (mid, lid, type) => {
    if (isNew) {
      // Auto-save the course as draft so the new lesson/block can attach assets.
      if (!course.title?.trim()) {
        toast.error("Add a course title first, then continue authoring");
        setShowAddBlock(false);
        return;
      }
      toast.info("Saving course as draft…");
      await save(false); // sets status=draft, navigates to /edit, replaces course state
      // After save() completes, the URL changes via navigate(); the lesson tree
      // was preserved by save() (course state is sent in payload). The user can
      // click Add again on the now-saved course.
      setShowAddBlock(false);
      return;
    }
    const block = { id: newId("blk"), type, payload: {} };
    if (type === "mcq") block.payload = { question: "", options: ["", ""], correct_options: [], multi_select: false };
    if (type === "image_gallery") block.payload = { images: [] };
    updateLesson(mid, lid, {
      blocks: [...((course.modules.find(m => m.id === mid)?.lessons || []).find(l => l.id === lid)?.blocks || []), block]
    });
    setShowAddBlock(false);
  };
  const updateBlock = (mid, lid, bid, newBlock) => {
    const less = course.modules.find(m => m.id === mid)?.lessons.find(l => l.id === lid);
    updateLesson(mid, lid, { blocks: (less?.blocks || []).map(b => b.id === bid ? newBlock : b) });
  };
  const deleteBlock = (mid, lid, bid) => {
    const less = course.modules.find(m => m.id === mid)?.lessons.find(l => l.id === lid);
    updateLesson(mid, lid, { blocks: (less?.blocks || []).filter(b => b.id !== bid) });
  };

  const totalLessons = course.modules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0);

  return (
    <div data-testid="course-editor">
      <Link to="/dashboard/courses?tab=mine" className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2">
        <ArrowLeft className="w-3.5 h-3.5" /> MY COURSES
      </Link>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">
            COURSE STUDIO · {course.status?.toUpperCase() || "DRAFT"}
          </div>
          <h1 className="font-serif text-3xl text-kb-ink mt-1 leading-tight">
            {isNew ? "Author a new" : "Edit"} <em className="italic text-kb-primary">course</em>
          </h1>
          <p className="text-xs text-kb-ink-muted mt-1">{totalLessons} lessons across {course.modules.length} modules</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => save()} disabled={saving} className="kb-btn-outline text-sm" data-testid="course-save-draft">
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save draft"}
          </button>
          {!isNew && courseId && (
            <a
              href={`/dashboard/courses/${courseId}`}
              target="_blank"
              rel="noreferrer"
              className="kb-btn-outline text-sm"
              data-testid="course-preview-btn"
            >
              <Eye className="w-3.5 h-3.5" /> Preview
            </a>
          )}
          {!isNew && (course.status === "draft" || course.status === "rejected" || !course.status) && (
            <button onClick={submitForReview} disabled={saving}
              className="kb-btn-primary text-sm" data-testid="course-submit-review-btn">
              <Eye className="w-3.5 h-3.5" /> Submit for review
            </button>
          )}
          {!isNew && course.status === "submitted_for_review" && (
            <div className="kb-mono text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 px-3 py-2 rounded-sm" data-testid="course-status-pending-review">
              ⏳ Awaiting admin review
            </div>
          )}
          {!isNew && course.status === "published" && (
            <div className="kb-mono text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 px-3 py-2 rounded-sm" data-testid="course-status-published">
              ✓ Published
            </div>
          )}
          {!isNew && course.status === "rejected" && (
            <div className="kb-mono text-[10px] uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-200 px-3 py-2 rounded-sm" title={course.review_note || ""} data-testid="course-status-rejected">
              ✕ Rejected — see note
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        {/* Left: Course meta + module/lesson outline */}
        <div className="lg:col-span-5 space-y-5">
          <div className="kb-card p-5">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">COURSE DETAILS</div>
            <div className="mt-3 space-y-2">
              <input className="kb-input text-sm" placeholder="Course title"
                value={course.title} onChange={(e) => setCourse({ ...course, title: e.target.value })}
                data-testid="course-title-input" />
              <textarea rows={3} className="kb-input resize-none text-sm" placeholder="Course description"
                value={course.description} onChange={(e) => setCourse({ ...course, description: e.target.value })}
                data-testid="course-description-input" />
              <div className="grid grid-cols-2 gap-2">
                <select className="kb-input text-sm" value={course.level}
                  onChange={(e) => setCourse({ ...course, level: e.target.value })}>
                  <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
                </select>
                <input type="number" min="0" className="kb-input text-sm" placeholder="Duration (hrs)"
                  value={course.duration_hours || 0}
                  onChange={(e) => setCourse({ ...course, duration_hours: (e.target.value === "" ? "" : (parseInt(e.target.value, 10) || 0)) })} />
              </div>
              <TagsInput
                value={course.tags || []}
                onChange={(tags) => setCourse({ ...course, tags })}
                placeholder="Tags (type and press Enter)"
                testId="course-tags"
                className="text-sm"
              />
              <input className="kb-input text-sm" placeholder="Cover image URL (or upload below)"
                value={course.cover_image_url || ""}
                onChange={(e) => setCourse({ ...course, cover_image_url: e.target.value })} />
              <div className="flex items-center gap-3">
                {course.cover_image_url && (
                  <img src={course.cover_image_url} alt="cover" className="w-24 h-14 object-cover rounded-sm border border-slate-200" data-testid="course-cover-preview" />
                )}
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary border border-kb-primary/30 px-3 py-1.5 rounded-sm cursor-pointer hover:bg-kb-primary/5 inline-flex items-center gap-1.5" data-testid="course-cover-upload-label">
                  Upload cover image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="course-cover-upload-input"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (!f) return;
                      if (!f.type.startsWith("image/")) { toast.error("Please pick an image"); return; }
                      if (f.size > 2 * 1024 * 1024) { toast.error("Image must be under 2 MB"); return; }
                      const dataUrl = await new Promise((resolve, reject) => {
                        const r = new FileReader();
                        r.onload = () => resolve(r.result);
                        r.onerror = reject;
                        r.readAsDataURL(f);
                      });
                      setCourse({ ...course, cover_image_url: dataUrl });
                      toast.success("Cover image attached — save to apply");
                    }}
                  />
                </label>
                {course.cover_image_url && (
                  <button type="button" onClick={() => setCourse({ ...course, cover_image_url: "" })} className="text-rose-500 hover:underline kb-mono text-[10px] uppercase" data-testid="course-cover-clear">Clear</button>
                )}
              </div>
            </div>
          </div>

          <div className="kb-card p-5">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PRICING & DISCOUNTS</div>
            <p className="text-xs text-kb-ink-muted mt-1">
              Admin reviews and finalises course pricing. Suggest a starting price below — final price + partner discounts are set by admin under Pricing & Coupons.
            </p>
            <div className="mt-3 space-y-2">
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Suggested price (NPR)</label>
                <input type="number" min="0" step="100" className="kb-input text-sm mt-1"
                  value={course.price_npr || 0}
                  onChange={(e) => setCourse({ ...course, price_npr: (e.target.value === "" ? "" : (parseFloat(e.target.value) || 0)) })}
                  data-testid="course-price-input" />
              </div>
              {course.pricing_status && (
                <div className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border inline-block ${
                  course.pricing_status === "approved" ? "border-emerald-200 bg-emerald-50 text-emerald-700" :
                  course.pricing_status === "rejected" ? "border-rose-200 bg-rose-50 text-rose-700" :
                  "border-amber-200 bg-amber-50 text-amber-700"
                }`}>
                  PRICING STATUS: {course.pricing_status}
                </div>
              )}
              {(course.partner_student_pct_off || course.partner_faculty_pct_off || course.free_for_partner_students) && (
                <div className="text-xs text-emerald-700 kb-mono uppercase tracking-wider">
                  Active discounts: {course.free_for_partner_students ? "FREE for partner students" : `${course.partner_student_pct_off || 0}% student · ${course.partner_faculty_pct_off || 0}% faculty`} (set by admin)
                </div>
              )}
            </div>
          </div>

          <div className="kb-card p-5">
            <div className="flex items-center justify-between">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">CURRICULUM</div>
              <button onClick={addModule} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1" data-testid="add-module-btn">
                <Plus className="w-3 h-3" /> Add module
              </button>
            </div>
            <div className="mt-3 space-y-2">
              {course.modules.length === 0 && <div className="text-sm text-kb-ink-muted text-center py-4">No modules yet.</div>}
              {course.modules.map((m, mi) => (
                <div key={m.id} className="border border-slate-200 rounded-sm">
                  <div className="flex items-center gap-2 p-2 bg-slate-50">
                    <button onClick={() => setOpenMod({ ...openMod, [m.id]: !openMod[m.id] })} className="text-kb-ink-muted">
                      {openMod[m.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                    </button>
                    <input className="bg-transparent flex-1 text-sm font-medium text-kb-ink outline-none"
                      value={m.title} onChange={(e) => updateModule(m.id, { title: e.target.value })} />
                    <button onClick={() => deleteModule(m.id)} className="text-rose-600 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {openMod[m.id] && (
                    <div className="p-2 space-y-1">
                      {(m.lessons || []).map((l, li) => (
                        <div key={l.id} className={`flex items-center gap-2 p-2 rounded-sm hover:bg-slate-50 cursor-pointer ${activeLes?.lid === l.id ? "bg-kb-primary/5 border border-kb-primary/20" : ""}`}
                          onClick={() => setActiveLes({ mid: m.id, lid: l.id })}>
                          <BookOpen className="w-3 h-3 text-kb-ink-muted" />
                          <input className="bg-transparent flex-1 text-xs text-kb-ink outline-none"
                            value={l.title} onChange={(e) => { e.stopPropagation(); updateLesson(m.id, l.id, { title: e.target.value }); }}
                            onClick={(e) => e.stopPropagation()} />
                          <span className="kb-mono text-[10px] text-kb-ink-light">{(l.blocks || []).length}b</span>
                          <button onClick={(e) => { e.stopPropagation(); deleteLesson(m.id, l.id); }} className="text-rose-600 p-1"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <button onClick={() => addLesson(m.id)} className="text-xs text-kb-primary kb-mono uppercase tracking-wider w-full text-left py-1 hover:bg-slate-50 px-2 rounded-sm">
                        + Add lesson
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Active lesson editor */}
        <div className="lg:col-span-7">
          {activeLes ? (() => {
            const m = course.modules.find(x => x.id === activeLes.mid);
            const l = m?.lessons.find(x => x.id === activeLes.lid);
            if (!l) return <div className="kb-card p-6 text-sm text-kb-ink-muted">Select a lesson on the left to edit.</div>;
            return (
              <div className="kb-card p-6 space-y-4">
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">LESSON EDITOR</div>
                <div className="grid grid-cols-12 gap-2">
                  <input className="kb-input text-sm col-span-9" placeholder="Lesson title"
                    value={l.title} onChange={(e) => updateLesson(m.id, l.id, { title: e.target.value })}
                    data-testid="lesson-title-input" />
                  <input type="number" className="kb-input text-sm col-span-3" placeholder="min"
                    value={l.duration_min || 10}
                    onChange={(e) => updateLesson(m.id, l.id, { duration_min: (e.target.value === "" ? "" : (parseInt(e.target.value, 10) || 10)) })} />
                </div>

                <div className="space-y-3">
                  {(l.blocks || []).map((b) => (
                    <BlockEditor key={b.id} block={b} courseId={courseId}
                      onChange={(nb) => updateBlock(m.id, l.id, b.id, nb)}
                      onDelete={() => deleteBlock(m.id, l.id, b.id)} />
                  ))}
                </div>

                <div>
                  {!showAddBlock ? (
                    <button onClick={() => setShowAddBlock(true)} className="kb-btn-primary text-sm w-full justify-center" data-testid="add-block-btn">
                      <Plus className="w-3.5 h-3.5" /> Add content block
                    </button>
                  ) : (
                    <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2">CHOOSE A BLOCK TYPE</div>
                      <div className="grid grid-cols-2 gap-2">
                        {BLOCK_TYPES.map(({ value, label, icon: Icon }) => (
                          <button key={value} onClick={() => addBlock(m.id, l.id, value)}
                            className="text-left text-xs border border-slate-200 bg-white rounded-sm p-2 hover:border-kb-primary inline-flex items-center gap-2"
                            data-testid={`add-block-${value}`}>
                            <Icon className="w-3.5 h-3.5 text-kb-primary" />
                            <span>{label}</span>
                          </button>
                        ))}
                      </div>
                      <button onClick={() => setShowAddBlock(false)} className="text-xs text-kb-ink-muted mt-2">Cancel</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })() : (
            <div className="kb-card p-6 text-sm text-kb-ink-muted text-center py-16">
              Create a module + lesson on the left, then select the lesson here to add content (text, video, quiz, etc.).
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
